// src/components/auth/components/MenuLogin.tsx
// Added authentication logic for login/logout while retaining menu structure
// Made OLS privacy a separate submenu and fixed duplicate key issue
// Fixed linear-gradient syntax in CSS
// Re-fixed linear-gradient syntax to ensure proper CSS parsing
import React from "react";
import styled from "styled-components";
import { Menu, Button } from "antd";
import { Link, useNavigate } from "react-router-dom";
import {
  PieChartOutlined,
  CompassOutlined,
  TeamOutlined,
  SolutionOutlined,
  UserOutlined,
  UserAddOutlined,
  ApiOutlined,
  AliwangwangOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Helmet } from "react-helmet-async";
import { useUserStore } from "../../../stores/UserStore";
import { useFirebase } from "../../dev/Firebase/Firebase";
import "./MenuLogin.css";

const MenuContainer = styled.div`
  display: flex;
  z-index: 101;
  align-items: flex-start;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  max-width: 200px;
  overflow: hidden;
`;

const MenuSection = styled.div`
  flex: 1;
`;

const StyledMenu = styled(Menu)`
  &.ant-menu-inline {
    border: none;
    background: linear-gradient(to bottom, #fff5e6, #fff);
    max-width: 200px;
    overflow-x: hidden;
  }

  .ant-menu-item {
    color: #0427f4 !important;
    transition: all 0.3s ease;
    &:hover {
      background: #ffaa00 !important;
      color: #fff !important;
      transform: translateX(5px); /* Subtle slide animation on hover */
    }
  }

  .ant-menu-submenu-title {
    color: #0427f4 !important;
    transition: all 0.3s ease;
    &:hover {
      background: #ffaa00 !important;
      color: #fff !important;
      transform: translateX(5px);
    }
  }
`;

const MenuItemButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: auto;
  padding: 8px 16px;
  border: none;
  background: none;
  color: #0427f4;
  &:hover {
    background: #f0f0f0;
    color: #1890ff;
  }
`;

const CloseButton = styled(Button)`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #ffcc00;
  border: none;
  color: #0427f4;
  font-size: 16px;
  transition: background 0.3s ease, transform 0.3s ease;
  &:hover {
    background: #ffaa00;
    color: #0427f4;
    transform: scale(1.05);
  }
  @media (min-width: 769px) {
    display: none; /* Hide on desktop */
  }
`;

interface MenuLoginProps {
  toggleCollapsed: () => void;
  collapsed: boolean;
}

const MenuLogin: React.FC<MenuLoginProps> = ({ toggleCollapsed, collapsed }) => {
  const userStore = useUserStore();
  const { signOut } = useFirebase();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut();
      userStore.clearUser();
      navigate('/login', { replace: true });
      if (!collapsed) toggleCollapsed();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const menuItems = [
    {
      key: "sub1",
      label: "OLS iee.aeo",
      children: [
        {
          key: "11",
          label: (
            <MenuItemButton as={Link} to="/lightScience" aria-label="onelightsystem">
              onelightsystem
            </MenuItemButton>
          ),
        },
        {
          key: "12",
          label: (
            <MenuItemButton as={Link} to="/OLSaeo" aria-label="OLS iee.aeo">
              OLS iee.aeo
            </MenuItemButton>
          ),
        },
        {
          key: "13",
          label: (
            <MenuItemButton as={Link} to="/OLShealth" aria-label="Health Longevity">
              Health Longevity
            </MenuItemButton>
          ),
        },
        {
          key: "14",
          label: (
            <MenuItemButton as={Link} to="/OLSaeo" aria-label="Atetaten">
              Atetaten
            </MenuItemButton>
          ),
        },
        {
          key: "15",
          label: (
            <MenuItemButton as={Link} to="/venus" aria-label="Venus">
              Venus
            </MenuItemButton>
          ),
        },
      ],
    },
    {
      key: "sub2",
      label: "OLSM",
      children: [
        {
          key: "21",
          icon: <TeamOutlined />,
          label: (
            <MenuItemButton as={Link} to="/aa" aria-label="OLS eae">
              OLS eae
            </MenuItemButton>
          ),
        },
        {
          key: "22",
          icon: <SolutionOutlined />,
          label: (
            <MenuItemButton as={Link} to="/rwh" aria-label="OLS inuo">
              OLS inuo
            </MenuItemButton>
          ),
        },
        {
          key: "23",
          icon: <UserOutlined />,
          label: (
            <MenuItemButton as={Link} to="/Stat" aria-label="OLS student">
              OLS student
            </MenuItemButton>
          ),
        },
        {
          key: "24",
          icon: <UserAddOutlined />,
          label: (
            <MenuItemButton as={Link} to="/OLSStudentProspect" aria-label="OLS prospect">
              OLS prospect
            </MenuItemButton>
          ),
        },
      ],
    },
    {
      key: "sub3",
      label: "OLS privacy",
      children: [
        {
          key: "31",
          icon: <AliwangwangOutlined />,
          label: (
            <MenuItemButton
              as={Link}
              to="/privacy"
              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
              aria-label="OLS privacy"
            >
              OLS privacy
            </MenuItemButton>
          ),
        },
        {
          key: "32",
          label: (
            <MenuItemButton as={Link} to="/olsdataprovision" aria-label="OLS data provision">
              OLSdataProvision
            </MenuItemButton>
          ),
        },
        {
          key: "33",
          icon: <CompassOutlined />,
          label: (
            <MenuItemButton as={Link} to="/OLScalendarTime" aria-label="OLS Calendar Time">
              OLS Calendar Time
            </MenuItemButton>
          ),
        },
        {
          key: "34",
          icon: <CompassOutlined />,
          label: (
            <MenuItemButton as={Link} to="/referral" aria-label="OLS promoterReferral">
              OLS promoterReferral
            </MenuItemButton>
          ),
        },
      ],
    },
    {
      key: "25",
      icon: <ApiOutlined />,
      label: (
        <MenuItemButton as={Link} to="/olsme" aria-label="OLSME">
          OLSME
        </MenuItemButton>
      ),
    },
    {
      key: "26",
      icon: <PieChartOutlined />,
      label: (
        <MenuItemButton as={Link} to="/aOLSLMasset" aria-label="OLSLM assets">
          OLSLM assets
        </MenuItemButton>
      ),
    },
    {
      key: "27",
      icon: <CompassOutlined />,
      label: (
        <MenuItemButton as={Link} to="/olsupdates" aria-label="OLS updates">
          OLS updates
        </MenuItemButton>
      ),
    },
    ...(userStore.isLoggedIn
      ? [
          {
            key: "profile",
            label: (
              <MenuItemButton as={Link} to="/home/profile" aria-label="Profile">
                Profile
              </MenuItemButton>
            ),
          },
          {
            key: "logout",
            label: (
              <MenuItemButton onClick={handleLogout} aria-label="Logout">
                Logout
              </MenuItemButton>
            ),
          },
        ]
      : [
          {
            key: "login",
            label: (
              <MenuItemButton as={Link} to="/login" aria-label="Login">
                Login
              </MenuItemButton>
            ),
          },
          {
            key: "register",
            label: (
              <MenuItemButton as={Link} to="/register" aria-label="Register">
                Register
              </MenuItemButton>
            ),
          },
        ]),
  ];

  return (
    <MenuContainer className="menu-container">
      <Helmet>
        <title>Navigation Menu - One Light System OLS</title>
        <meta
          name="description"
          content="Navigate One Light System OLS for Sun Light Meditation education and join the Light Civilization."
        />
        <meta
          name="keywords"
          content="OLS Navigation, Sun Light Meditation, OLS Meditation Education, Light Civilization"
        />
        <link rel="canonical" href="https://www.olsme.com/menu" />
      </Helmet>
      <CloseButton
        icon={<CloseOutlined />}
        onClick={toggleCollapsed}
        aria-label="Close menu"
      />
      <MenuSection>
        <StyledMenu
          mode="inline"
          inlineCollapsed={collapsed}
          items={menuItems}
          onClick={toggleCollapsed} // Close menu on item click
        />
      </MenuSection>
    </MenuContainer>
  );
};

export default React.memo(MenuLogin);